import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchLayoutSourceFiles,
  uploadLayoutSourceFile,
  deleteLayoutSourceFile,
  type LayoutSourceFile,
  type LayoutSourceFilesStatus,
  type LayoutSourceKind,
} from '../../lib/adminApi';
import { FileDropzone, cls } from './shared';

type SlotConfig = {
  kind: LayoutSourceKind;
  title: string;
  blurb: string;
  accept: string;
  validate: (file: File) => boolean;
  typeError: string;
  idleLabel: string;
  replaceLabel: string;
  openLabel: string;
};

const SLOTS: SlotConfig[] = [
  {
    kind: 'dimensional-pdf',
    title: '2D dimensional PDF',
    blurb: 'The single-page dimensional drawing PDF (the catalog product page). Stored exactly as dropped; the Visualizer extracts the linework.',
    accept: 'application/pdf,.pdf',
    validate: (f) => /\.pdf$/i.test(f.name) || f.type === 'application/pdf',
    typeError: 'Please choose a PDF file.',
    idleLabel: 'Drop a PDF here',
    replaceLabel: 'Drop a PDF to replace',
    openLabel: 'Open PDF',
  },
  {
    kind: 'isometric',
    title: 'Hi-res isometric',
    blurb: 'The high-resolution isometric render (~6K PNG or JPG). Stored exactly as dropped; the Visualizer uses it as the pipeline source image.',
    accept: 'image/png,image/jpeg,.png,.jpg,.jpeg',
    validate: (f) => /\.(png|jpe?g)$/i.test(f.name) || f.type === 'image/png' || f.type === 'image/jpeg',
    typeError: 'Please choose a PNG or JPG image.',
    idleLabel: 'Drop a PNG or JPG here',
    replaceLabel: 'Drop a PNG or JPG to replace',
    openLabel: 'Open image',
  },
];

export function SourceFilesTab() {
  const { id } = useParams<{ id: string }>();
  const layoutId = parseInt(id!, 10);
  const [status, setStatus] = useState<LayoutSourceFilesStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function load() {
    fetchLayoutSourceFiles(layoutId)
      .then((s) => { setStatus(s); setLoading(false); })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : 'Failed to load'); setLoading(false); });
  }
  useEffect(load, [layoutId]);

  if (loading) return <p className="text-vari-gray text-sm">Loading…</p>;
  if (error || !status) return <p className="text-red-600 text-sm">{error ?? 'Failed to load'}</p>;

  return (
    <>
      <p className="text-sm text-vari-gray mb-6 max-w-2xl">
        Source files the Product Visualizer fetches for this layout. Each is stored exactly as dropped
        and exposed to the integration with a content hash — re-dropping a file updates the hash, which
        is how the Visualizer knows to reprocess.
      </p>
      <div className="grid gap-5 sm:grid-cols-2 max-w-4xl">
        {SLOTS.map((slot) => (
          <SourceSlot
            key={slot.kind}
            layoutId={layoutId}
            slot={slot}
            file={slot.kind === 'isometric' ? status.isometric : status.dimensionalPdf}
            onChange={load}
          />
        ))}
      </div>
    </>
  );
}

function SourceSlot({
  layoutId,
  slot,
  file,
  onChange,
}: {
  layoutId: number;
  slot: SlotConfig;
  file: LayoutSourceFile | null;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const available = file != null;

  async function doUpload(f: File) {
    if (!slot.validate(f)) { alert(slot.typeError); return; }
    setBusy(true);
    try {
      await uploadLayoutSourceFile(layoutId, slot.kind, f);
      onChange();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  }

  async function doRemove() {
    if (!confirm(`Remove the ${slot.title}? The blob in Azure Storage will NOT be deleted.`)) return;
    setBusy(true);
    try {
      await deleteLayoutSourceFile(layoutId, slot.kind);
      onChange();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Remove failed');
    } finally {
      setBusy(false);
    }
  }

  async function copyHash() {
    if (!file) return;
    try {
      await navigator.clipboard.writeText(file.hash);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <div className="rounded-lg border border-vari-soft-gray bg-white shadow-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-vari-navy">{slot.title}</h2>
        <span
          className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
            available ? 'bg-vari-green/40 text-vari-navy' : 'bg-vari-soft-gray/40 text-vari-gray'
          }`}
        >
          {available ? 'Stored' : 'Not uploaded'}
        </span>
      </div>
      <p className="text-xs text-vari-gray">{slot.blurb}</p>

      <FileDropzone
        accept={slot.accept}
        busy={busy}
        available={available}
        onFile={(f) => void doUpload(f)}
        idleLabel={slot.idleLabel}
        replaceLabel={slot.replaceLabel}
      />

      {available && file ? (
        <div className="space-y-2.5">
          <p className="font-mono text-[11px] break-all text-vari-gray">
            SHA-256 <span className="text-vari-navy" title={file.hash}>{file.hash.slice(0, 12)}…</span>
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => void copyHash()} className={cls.btnOutline}>
              {copied ? '✓ Hash copied' : 'Copy hash'}
            </button>
            {file.url && (
              <a href={file.url} target="_blank" rel="noreferrer" className={cls.btnOutline}>
                {slot.openLabel}
              </a>
            )}
            <button type="button" onClick={() => void doRemove()} disabled={busy} className={cls.btnDanger}>
              Remove
            </button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-vari-gray">No file uploaded yet.</p>
      )}
    </div>
  );
}
